import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CurrencyInput } from '@components/core-ui/form-elements/CurrencyInput';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { getApiCallErrorMessage } from 'app/http/getApiCallErrorMessage';
import { useState } from 'react';

import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './AddBalanceToBillingAccount/styles/Content';

type TProps = {
  billingAccountId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AdminAddBalanceToBillingAccountModal = ({ billingAccountId, onCancel, onSuccess }: TProps) => {
  const { closeModal } = useModalContext();
  const { call } = useAPI();
  const [amount, setAmount] = useState(10);
  const [error, setError] = useState<string | null>(null);

  const addBalanceMutation = useMutation({
    mutationFn: () =>
      call(() =>
        AdminService.addMainBalanceToBillingAccount({
          baId: billingAccountId,
          requestBody: { amount: amount * 100 },
        }),
      ),
    onSuccess: (result) => {
      if (result?.error) {
        setError(result.message ?? result.error);
        return;
      }
      closeModal();
      onSuccess();
    },
    onError: (mutationError) => {
      setError(mutationError instanceof ApiCallError ? getApiCallErrorMessage(mutationError) : 'An error occurred');
    },
  });

  const onSubmit = () => {
    setError(null);
    addBalanceMutation.mutate();
  };

  const isLoading = addBalanceMutation.isPending;
  const isSubmitDisabled = isLoading || amount === 0 || amount > 50;

  return (
    <ModalWrapper width="400px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Add Goodwill Credit</ModalTitle>
      <Content>
        <div className="input-wrapper">
          <div className="label">Amount (USD)</div>
          <div className="description">
            Maximum amount is $50.00. <br />
            This will be added to the separate Free Credit balance
          </div>
          <div className="flex flex-row">
            <CurrencyInput
              onChange={(v) => {
                setError(null);
                if (!v) {
                  setAmount(0);
                  return;
                }
                setAmount(v);
              }}
              label="Amount"
              value={amount}
            />
          </div>
        </div>

        {error ? (
          <div className="error">
            <p>{error}</p>
          </div>
        ) : null}
        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isSubmitDisabled} onClick={onSubmit}>
            Add funds
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
